// FAR-77: the Next 3 Months tab. Pure, so its rules -- which months, labels,
// search, filters, the bulk actions and their kept-edit counts -- are tested
// without a browser. Shared maths (utilization, DOS risk, footer, DOS status)
// lives in fourToTwelve.js and is reused as-is.

import { filterRows, horizonMonths } from './fourToTwelve.js'

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const label = (iso) => {
  const [y, m] = String(iso).split('-')
  return `${SHORT[Number(m) - 1] || m} ${y}`
}

// §2: the plan's first three months.
export function nextThreeMonths(months) {
  return (months || []).slice(0, 3)
}

// FAR-77 §2 / FAR-76 §1 tab labels from the plan's months:
// "Oct 2026 → Dec 2026" and "Jan 2027 → Mar 2028". null until months load.
export function tabLabels(months) {
  const near = nextThreeMonths(months)
  const far = horizonMonths(months)
  const span = (ms) => (ms.length === 0 ? null : ms.length === 1 ? label(ms[0].iso) : `${label(ms[0].iso)} → ${label(ms[ms.length - 1].iso)}`)
  return { '3month': span(near), '4-12month': span(far) }
}

// §2: "· rest of month" on the first month when the Base Plan started mid-month.
export function monthHeading(month, index, basePlan) {
  const partial = index === 0 && basePlan?.startDate?.startsWith(month.iso)
    && basePlan.firstMonthFraction > 0 && basePlan.firstMonthFraction < 1
  return partial ? `${month.label} · rest of month` : month.label
}

// §5.3 search: description, item number or Brite code, case-insensitive.
export function matchesSearch(row, query) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return true
  return [row.description, row.itemNumber, row.briteCode]
    .some((v) => String(v ?? '').toLowerCase().includes(q))
}

// §5.3 filters: line pill, status (All / Below Min / Above Max) and search.
export function visibleRows(rows, months, { line = '', status = 'all', query = '' } = {}) {
  return filterRows(rows, months, { line, status }).filter((r) => matchesSearch(r, query))
}

// FAR-83 §4.2 / FAR-77 §5.5: the four sub-columns per month, each sortable.
// `field` is the row key suffix: row[`${month.key}_${field}`].
export const SUB_COLS = [
  { label: 'DEMAND', field: 'fcst' },
  { label: 'UNITS', field: 'cases' },
  { label: 'INVENTORY', field: 'endInv' },
  { label: 'DOS', field: 'dos' },
]

export const subSortKey = (month, sub) => `${month.key}_${sub.field}`

// FAR-83 §4.2 / FAR-77 §5.5: the Item column sorts ascending by default.
export const DEFAULT_SORT = { key: 'description', dir: 'asc' }

// §5.4 sorting by a column; text columns A–Z, numbers low→high; asc/desc.
export function sortRows(rows, key, dir = 'asc') {
  if (!key) return rows
  const sign = dir === 'desc' ? -1 : 1
  return [...rows].sort((a, b) => {
    const x = a[key]
    const y = b[key]
    if (x == null && y == null) return 0
    if (x == null) return 1 // blanks last either way
    if (y == null) return -1
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * sign
    return String(x).localeCompare(String(y), undefined, { numeric: true }) * sign
  })
}

// §5.6 bulk bar: SKUs it acts on -- checked and visible (Open Item 7, as on
// 4–18), never made-to-order (their checkbox is disabled).
export function bulkTargets(rows, selected) {
  return (rows || []).filter((r) => selected?.has(r.itemNumber) && !r.isMadeToOrder).map((r) => r.itemNumber)
}

export const ACTIONS = [
  { key: 'increase', label: 'Increase units' },
  { key: 'dos', label: 'Set DOS to' },
]

// FAR-88 T3 / FAR-90 §6: the percent is typed, not picked. The limits are the
// API's (handlers/modifications.go): Increase units -100..200 (negative
// reduces), Set DOS 0..200. Decimals are fine ("12.5"). A leading "+" and a
// trailing "%" are allowed, as the box shows "+10 %". Returns the number, or
// null when the text isn't a percent in range (Apply stays disabled).
export const PCT_LIMITS = { increase: [-100, 200], dos: [0, 200] }
export function parsePercent(text, action = 'increase') {
  const s = String(text ?? '').trim().replace(/\s*%$/, '').replace(/^\+/, '').trim()
  if (!/^-?(\d+(\.\d*)?|\.\d+)$/.test(s)) return null
  const v = Number(s)
  const [lo, hi] = PCT_LIMITS[action] || PCT_LIMITS.increase
  return v >= lo && v <= hi ? v : null
}

// The months a bulk action covers: all three, or one.
export function scopeMonths(months, scope) {
  if (!scope || scope === 'all') return months || []
  return (months || []).filter((m) => m.iso === scope)
}

// §5.6: typed Units in scope are kept; the confirmation says how many.
export function keptEditCount(cells, items, months) {
  const want = new Set((items || []).map(String))
  const isos = new Set((months || []).map((m) => m.iso))
  let n = 0
  for (const c of cells || []) {
    if (c.changeType !== 'Cell' || c.isIgnored || !want.has(String(c.identifier))) continue
    for (const iso of Object.keys(c.monthValues || {})) if (isos.has(iso)) n++
  }
  return n
}

// The rule a bulk action saves. One modification per Apply (§5.6).
export function bulkRule({ action, pct, basis, items, months, scope }) {
  const inScope = scopeMonths(months, scope)
  const monthValues = Object.fromEntries(inScope.map((m) => [m.iso, pct]))
  const where = scope && scope !== 'all' ? `in ${label(scope)}` : 'in all months'
  const skus = `${items.length} SKU${items.length === 1 ? '' : 's'}`
  if (action === 'increase') {
    return {
      name: `Increase units by ${pct}% ${where} · ${skus}`,
      scope: 'Items', items: [...items], changeType: 'UnitsPercent', monthValues,
    }
  }
  return {
    name: `${pct}% of ${basis} DOS ${where} · ${skus}`,
    scope: 'Items', items: [...items], basis, changeType: 'DOSTarget', monthValues,
  }
}

// FAR-83 §4.5: "Recorded to {Modification}; 3 SKUs, 2 edited cells kept".
export function appliedMessage(skuCount, kept, modificationName) {
  const where = modificationName ? `Recorded to ${modificationName}` : 'Recorded to the Modification'
  const skus = `${skuCount} SKU${skuCount === 1 ? '' : 's'}`
  return kept > 0 ? `${where}; ${skus}, ${kept} edited cell${kept === 1 ? '' : 's'} kept` : `${where}; ${skus}`
}

// FAR-83 §4.4 / FAR-77 §5.3 review hover text, the spec's wording.
export function reviewTooltip(n) {
  return `${n} edit${n === 1 ? '' : 's'} need${n === 1 ? 's' : ''} review — The Base Plan changed since this edit was typed. `
    + 'The typed value now puts DOS outside the band. Leave it to keep it, or send it back.'
}

// FAR-83 §4.6: totals abbreviated in thousands where the column is narrow
// ("1,344K"). Below 10,000 the full number reads fine.
export function abbreviate(v) {
  if (v == null || Number.isNaN(v)) return '—'
  const r = Math.round(v)
  if (Math.abs(r) < 10000) return r.toLocaleString('en-US')
  return `${Math.round(r / 1000).toLocaleString('en-US')}K`
}

// FAR-83 §4.3 / FAR-77 §5.6: an out-of-band DOS cell is tinted (red below
// Min, amber above Max, as in the mockup), over the month's blue tint. In
// band or unbanded: no tint of its own.
export const DOS_TINT = {
  below: { bgcolor: '#FDE2E1', color: '#B91C1C' },
  above: { bgcolor: '#FEF3C7', color: '#B45309' },
}
export function dosCellStyle(status) {
  return DOS_TINT[status] || null
}

// FAR-83 §4.6 / FAR-77 §5.9: Δ vs Base Plan is green when different, gray
// "0" when unchanged (and gray "—" when unknown).
export function deltaColor(delta) {
  return delta ? '#15803D' : 'text.secondary'
}

// FAR-83 §4.2: the header checkbox is tri-state over the shown, selectable rows.
export function selectAllState(rows, selected) {
  const n = (rows || []).filter((r) => selected?.has(r.itemNumber)).length
  if (!rows?.length || n === 0) return 'none'
  return n === rows.length ? 'all' : 'some'
}

// FAR-76 §4.3 / FAR-77 §5.5 / FAR-83 §4.3: a DEMAND cell is blue when the
// forecast moved since the Base Plan. changes is GET /base-plans/{id}/
// forecast-changes' map: material -> month -> { basePlan, latest }.
export function demandChange(changes, itemNumber, iso) {
  const c = changes?.[itemNumber]?.[iso]
  if (!c) return null
  const diff = c.latest - c.basePlan
  const pct = c.basePlan ? Math.round((diff / c.basePlan) * 100) : null
  const n = (v) => Math.round(v).toLocaleString('en-US')
  return {
    ...c,
    // FAR-83 §4.3: "Demand forecast up 8% since the Base Plan" (direction and
    // percent), with the two values for context.
    tooltip: `Demand forecast ${diff >= 0 ? 'up' : 'down'} ${pct == null ? `from 0` : `${Math.abs(pct)}%`} since the Base Plan (${n(c.basePlan)} → ${n(c.latest)})`,
  }
}
